import CloseIcon from "@mui/icons-material/Close";
import { IconButton } from "@mui/material";
import StewartInput from "controls/global/stewart-input";
import React, { useState } from "react";
import DecimalNumberFormat from "../../policy-management/shared/DecimalNumberFormat";
import PeriodNumberFormat from "../shared/PeriodNumberFormat";
import { PERIOD_FORMAT_ERROR, PERIOD_REGEX } from "../shared/dialogHelper";

interface Props {
  // A PremiumReceivableCriteria property name, not necessarily a column field —
  // the grid maps some columns onto differently-named criteria keys.
  name: string;
  disabled?: boolean;
  onChange: (name: string, value: string | null) => void;
  defaultValue?: string;
  placeholder?: string;
  // Opt-in only: this also backs the free-text AgentID header search, so the
  // numeric mask must never be unconditional.
  numeric?: boolean;
  decimalScale?: number;
  // MM/YYYY mask for the Accounting Period / Policy Month-Year searches, with
  // an incomplete entry flagged on blur.
  period?: boolean;
}

const InputSearch = ({
  name,
  disabled,
  onChange,
  defaultValue,
  placeholder = "Search",
  numeric,
  decimalScale = 2,
  period,
}: Props) => {
  const [value, setValue] = useState<string>(defaultValue ? defaultValue : "");
  const [hasError, setHasError] = useState<boolean>(false);

  const clearInput = () => {
    setValue("");
    setHasError(false);
    // Straight to the parent with the `name` prop, rather than mutating the DOM
    // node and re-entering handleInputChange through it. That indirection could
    // drop the clear silently — a null ref (the masked variants hand theirs over
    // indirectly, via react-number-format's getInputRef), a DOM node whose name
    // doesn't reach the handler, or the stale-value guard below — leaving the
    // old criteria in place while the box looked empty. Writing to the DOM was
    // redundant anyway: the field is controlled on `value`.
    onChange(name, "");
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const fieldName = event.target.name;
    const currentValue = event.target.value;
    if (value !== event.target.value.trim()) {
      onChange(fieldName, currentValue);
    }
    setValue(currentValue || "");
    // Clear a standing error as soon as the user resumes typing; blur decides
    // whether to flag it again.
    setHasError(false);
  };

  // A partial period ("04/20") converts to null and is silently dropped from
  // the criteria, which would otherwise leave Apply Filters disabled with no
  // explanation. Flag it instead. Mirrors DatePickerSearch's blur validation.
  const handleBlur = () => {
    if (!period) return;
    setHasError(!!value && !PERIOD_REGEX.test(value));
  };

  return (
    <StewartInput
      {...{ name }}
      id={name}
      fullWidth
      onChange={handleInputChange}
      onBlur={handleBlur}
      disabled={disabled}
      placeholder={placeholder}
      // The header actions row is a fixed 56px, so the error is shown as a red
      // outline plus a native tooltip rather than helperText, which would push
      // the row taller. Same treatment DatePickerSearch uses.
      error={hasError}
      title={hasError ? PERIOD_FORMAT_ERROR : undefined}
      InputProps={{
        endAdornment: !!value && (
          <IconButton onClick={clearInput} disabled={disabled}>
            <CloseIcon />
          </IconButton>
        ),
        ...(numeric ? { inputComponent: DecimalNumberFormat as any } : {}),
        ...(period ? { inputComponent: PeriodNumberFormat as any } : {}),
      }}
      {...(numeric ? { inputProps: { decimalScale } } : {})}
      value={value}
    />
  );
};

export default InputSearch;
